import { FC } from "react";
import { formatNumberWithSuffix } from "../../../../../common/Number";
import { isCancelledDeal, isOpenDeal, OrderForm } from "../../../../../types/OrderFormTypes";
import classes from "./DealSavings.module.less";

// bigint columns arrive serialized as strings
const cents = (value: number | string | undefined | null) => (value != null ? Number(value) : null);
// Whole units only: "$8M", "$125K".
const formatCents = (value: number) => `$${formatNumberWithSuffix(value / 100, 0)}`;

export interface SavingsProgressProps {
    /** Targeted savings in cents. */
    target?: number | string | null;
    /** Savings realized on signed deals, in cents: banked. */
    realized?: number | string | null;
    /** Savings realized so far on deals still open, in cents: on the table, not banked. */
    pending?: number | string | null;
}

/**
 * Savings against a target: "$realized of $target" on the left (realized
 * dark, target light), the realized share of the target on the right, and a
 * thin progress bar under both. The bar fills solid green for the banked
 * share and hatched for the pending share, side by side. The amounts and
 * the percentage count both. Only the realized amount while there is no
 * target; a dash until either exists.
 *
 * Plain spans on purpose: a spend tree renders one of these per row, and
 * antd's Typography was most of the cost of expanding it.
 */
export const SavingsProgress: FC<SavingsProgressProps> = (props) => {
    const target = cents(props.target);
    const banked = cents(props.realized) ?? 0;
    const pending = cents(props.pending) ?? 0;
    const realized = banked + pending;
    const hasTarget = target != null && target > 0;
    if (!hasTarget && realized <= 0) return <span className={classes.dash}>-</span>;
    if (!hasTarget) {
        return (
            <span className={classes.strong} style={{ fontSize: 12 }}>
                {formatCents(realized)}
            </span>
        );
    }
    const share = (amount: number) => Math.min((amount / target!) * 100, 100);
    const percent = Math.round((realized / target!) * 100);
    return (
        <div className={classes.savings}>
            <div className={classes.amounts}>
                <span>
                    <span className={classes.strong}>{formatCents(realized)}</span>
                    <span className={classes.light}> of {formatCents(target!)}</span>
                </span>
                <span className={classes.strong}>{percent}%</span>
            </div>
            <div className={classes.track}>
                <div>
                    <div className={classes.fill} style={{ width: `${share(banked)}%` }} />
                    <div
                        className={classes["fill--pending"]}
                        style={{ width: `${share(realized) - share(banked)}%` }}
                    />
                </div>
            </div>
        </div>
    );
};

export interface MeasureProgressProps {
    /** The number, bold on the left: "72%". */
    value: string;
    /** A light reading on the right: "8,900 of 12,400". */
    detail?: string;
    /** The bar's fill, 0 to 100. */
    percent: number;
    color: "green" | "orange" | "red";
}

/**
 * Any measure against a bar, in the savings' two rows: the number and a
 * reading, then a 4px bar colored by how the measure scores. A utilization
 * beside a SavingsProgress sits level with it.
 */
export const MeasureProgress: FC<MeasureProgressProps> = ({ value, detail, percent, color }) => (
    <div className={classes.savings}>
        <div className={classes.amounts}>
            <span className={classes.strong}>{value}</span>
            {detail && <span className={classes.light}>{detail}</span>}
        </div>
        <div className={classes.track}>
            <div>
                <div className={classes[`fill--${color}`]} style={{ width: `${Math.min(100, Math.max(0, percent))}%` }} />
            </div>
        </div>
    </div>
);

/**
 * The savings a cancelled deal had identified: just the target, labelled, since
 * nothing was or will be realized against it. A dash when there was none.
 */
export const IdentifiedSavings: FC<{ target?: number | string | null }> = (props) => {
    const target = cents(props.target);
    if (target == null || target <= 0) return <span className={classes.dash}>-</span>;
    return (
        <span style={{ fontSize: 12 }}>
            <span className={classes.strong}>{formatCents(target)}</span>
            <span className={classes.identified}> identified</span>
        </span>
    );
};

/** The deal's savings against its target, or what it had identified once cancelled. */
export const Savings: FC<{ deal: OrderForm }> = ({ deal }) => {
    if (isCancelledDeal(deal)) return <IdentifiedSavings target={deal.targetedSavings} />;
    // An open deal's realized savings are all still pending.
    const open = isOpenDeal(deal);
    return (
        <SavingsProgress
            target={deal.targetedSavings}
            realized={open ? 0 : deal.realizedSavings}
            pending={open ? deal.realizedSavings : 0}
        />
    );
};
